// Shared icon registry for Category navigation (CasmaShop — cosmetics & personal care).
//
// Uses react-icons:
// - `gi` (game-icons.net set) only for its beauty/personal-care glyphs —
//   lipstick, perfume, soap, comb, mirror, flowers, etc.
// - `lu` (Lucide) for clean utility & commerce icons — cart, wallet, droplet, etc.
// - `fa6` (Font Awesome 6) for app-store / OS brands.
//
// The string `name` here is stored in the Category model and is the key both
// the admin picker and the public web nav use to look up the component. Keep
// keys identical to figano-web/src/utils/categoryIconRegistry.ts.
import type { ComponentType, CSSProperties } from 'react';
import type { IconType } from 'react-icons';

// Beauty & personal-care glyphs
import {
  GiLipstick,
  GiPerfumeBottle,
  GiDelicatePerfume,
  GiSoap,
  GiSoapExperiment,
  GiComb,
  GiMirrorMirror,
  GiBeard,
  GiRazor,
  GiHairStrands,
  GiEyelashes,
  GiLotus,
  GiRose,
  GiSunflower,
  GiFlowerPot,
  GiFlowerEmblem,
  GiHerbsBundle,
  GiLeafSwirl,
  GiSpray,
  GiTowel,
  GiShower,
  GiBathtub,
  GiToothbrush,
  GiWaterDrop,
  GiDropletSplash,
  GiSunglasses,
  GiPearlNecklace,
  GiHandBag,
  GiFemale,
  GiMale,
  GiBabyBottle,
  GiMedicines,
  GiHealthNormal,
  GiBodyBalance,
  GiCutDiamond,
  GiDiamonds,
  GiSparkles,
  GiCrown,
  GiPaintBrush,
  GiPalette,
  GiClothes,
  GiPartyPopper,
  GiSunRadiations,
  GiSnowflake1,
  GiFlame,
  GiKey,
  GiPadlock,
  GiBookmark,
  GiInfo,
  GiHelp,
  GiCheckMark,
  GiMagnifyingGlass,
} from 'react-icons/gi';

// Font Awesome 6 (app-store / OS brands)
import {
  FaApple,
  FaAndroid,
  FaGooglePlay,
  FaAppStoreIos,
} from 'react-icons/fa6';

// Lucide (utility / commerce / generic)
import {
  LuSparkles,
  LuDroplet,
  LuDroplets,
  LuFlower,
  LuFlower2,
  LuLeaf,
  LuSprout,
  LuSun,
  LuMoon,
  LuBath,
  LuShowerHead,
  LuScissors,
  LuSprayCan,
  LuGem,
  LuBaby,
  LuSmile,
  LuHeartPulse,
  LuPill,
  LuBrush,
  LuPaintbrush,
  LuHand,
  LuHandHeart,
  LuCrown,
  LuFeather,
  LuPalette,
  LuPipette,
  LuFlaskConical,
  LuTestTube,
  LuWandSparkles,
  LuBadgeCheck,
  LuTruck,
  LuCitrus,
  LuCherry,
  LuSmartphone,
  LuShoppingCart,
  LuShoppingBag,
  LuStore,
  LuTag,
  LuPercent,
  LuGift,
  LuTicket,
  LuWallet,
  LuCreditCard,
  LuBanknote,
  LuTrendingUp,
  LuFlame,
  LuZap,
  LuStar,
  LuHeart,
  LuBookmark,
  LuBell,
  LuMessageCircle,
  LuMail,
  LuPhone,
  LuUser,
  LuUsers,
  LuShield,
  LuLock,
  LuSettings,
  LuHouse,
  LuBookOpen,
  LuFileText,
  LuImage,
  LuGlobe,
  LuCoffee,
  LuDumbbell,
  LuCloudDownload,
} from 'react-icons/lu';

// react-icons `IconType` returns `ReactNode`, which newer `@types/react`
// rejects when used as a JSX component. We re-cast the lookup return type to
// a plain `ComponentType` so consumers can render `<Icon size={...} />`
// without TypeScript complaining.
export type CategoryIconComponent = ComponentType<{
  size?: number | string;
  color?: string;
  className?: string;
  style?: CSSProperties;
}>;

export interface IconEntry {
  name: string;
  component: IconType;
  keywords: string[];
}

// Curated set ordered so the picker grid leads with the most useful icons.
export const ICON_REGISTRY: IconEntry[] = [
  // --- Makeup ---
  { name: 'gi-lipstick', component: GiLipstick, keywords: ['lipstick', 'makeup', 'lip', 'رژ لب', 'آرایش', 'لب'] },
  { name: 'gi-eyelashes', component: GiEyelashes, keywords: ['mascara', 'eyelash', 'eye', 'ریمل', 'مژه', 'چشم'] },
  { name: 'gi-paint-brush', component: GiPaintBrush, keywords: ['brush', 'makeup', 'foundation', 'براش', 'قلم‌مو', 'کرم پودر'] },
  { name: 'lu-brush', component: LuBrush, keywords: ['brush', 'blush', 'براش', 'رژگونه'] },
  { name: 'lu-paintbrush', component: LuPaintbrush, keywords: ['brush', 'eyeliner', 'خط چشم', 'قلم'] },
  { name: 'gi-palette', component: GiPalette, keywords: ['palette', 'eyeshadow', 'color', 'پالت', 'سایه چشم'] },
  { name: 'lu-palette', component: LuPalette, keywords: ['palette', 'color', 'پالت', 'رنگ'] },
  { name: 'gi-mirror', component: GiMirrorMirror, keywords: ['mirror', 'makeup', 'beauty', 'آینه', 'زیبایی'] },
  { name: 'lu-wand-sparkles', component: LuWandSparkles, keywords: ['beauty', 'glow', 'magic', 'زیبایی', 'درخشش'] },

  // --- Skin care ---
  { name: 'lu-droplet', component: LuDroplet, keywords: ['serum', 'moisturizer', 'hydration', 'سرم', 'آبرسان', 'مرطوب‌کننده'] },
  { name: 'lu-droplets', component: LuDroplets, keywords: ['toner', 'micellar', 'water', 'تونر', 'میسلار', 'پاک‌کننده'] },
  { name: 'gi-water-drop', component: GiWaterDrop, keywords: ['water', 'hydration', 'آبرسان', 'قطره'] },
  { name: 'gi-droplet-splash', component: GiDropletSplash, keywords: ['cleanser', 'face wash', 'ژل شستشو', 'پاک‌کننده'] },
  { name: 'lu-pipette', component: LuPipette, keywords: ['serum', 'dropper', 'vitamin c', 'سرم', 'ویتامین سی'] },
  { name: 'lu-flask', component: LuFlaskConical, keywords: ['serum', 'ampoule', 'treatment', 'آمپول', 'درمانی'] },
  { name: 'lu-test-tube', component: LuTestTube, keywords: ['sample', 'tester', 'نمونه', 'تستر'] },
  { name: 'lu-sun', component: LuSun, keywords: ['sunscreen', 'spf', 'ضدآفتاب', 'آفتاب'] },
  { name: 'gi-sun', component: GiSunRadiations, keywords: ['sunscreen', 'spf', 'ضدآفتاب'] },
  { name: 'lu-moon', component: LuMoon, keywords: ['night cream', 'night', 'کرم شب', 'شب'] },
  { name: 'lu-smile', component: LuSmile, keywords: ['face', 'skin', 'صورت', 'پوست'] },
  { name: 'lu-citrus', component: LuCitrus, keywords: ['vitamin c', 'brightening', 'ویتامین سی', 'روشن‌کننده'] },
  { name: 'gi-snowflake', component: GiSnowflake1, keywords: ['cooling', 'soothing', 'خنک‌کننده', 'تسکین'] },

  // --- Hair care ---
  { name: 'gi-hair-strands', component: GiHairStrands, keywords: ['hair', 'shampoo', 'مو', 'شامپو'] },
  { name: 'gi-comb', component: GiComb, keywords: ['comb', 'hair', 'شانه', 'مو'] },
  { name: 'lu-scissors', component: LuScissors, keywords: ['scissors', 'salon', 'قیچی', 'آرایشگاه'] },
  { name: 'lu-spray-can', component: LuSprayCan, keywords: ['spray', 'hair spray', 'اسپری', 'تافت'] },

  // --- Fragrance ---
  { name: 'gi-perfume-bottle', component: GiPerfumeBottle, keywords: ['perfume', 'fragrance', 'عطر', 'ادکلن'] },
  { name: 'gi-delicate-perfume', component: GiDelicatePerfume, keywords: ['perfume', 'women', 'عطر', 'زنانه'] },
  { name: 'gi-spray', component: GiSpray, keywords: ['body spray', 'deodorant', 'بادی اسپلش', 'اسپری', 'دئودورانت'] },

  // --- Body & bath ---
  { name: 'gi-soap', component: GiSoap, keywords: ['soap', 'body wash', 'صابون', 'شامپو بدن'] },
  { name: 'gi-soap-experiment', component: GiSoapExperiment, keywords: ['foam', 'bubble', 'فوم', 'کف'] },
  { name: 'lu-bath', component: LuBath, keywords: ['bath', 'body', 'حمام', 'بدن'] },
  { name: 'lu-shower-head', component: LuShowerHead, keywords: ['shower', 'دوش', 'حمام'] },
  { name: 'gi-shower', component: GiShower, keywords: ['shower', 'دوش'] },
  { name: 'gi-bathtub', component: GiBathtub, keywords: ['bath', 'وان', 'حمام'] },
  { name: 'gi-towel', component: GiTowel, keywords: ['towel', 'spa', 'حوله', 'اسپا'] },
  { name: 'lu-hand', component: LuHand, keywords: ['hand cream', 'nail', 'کرم دست', 'ناخن'] },
  { name: 'lu-hand-heart', component: LuHandHeart, keywords: ['care', 'gentle', 'مراقبت', 'ملایم'] },
  { name: 'gi-body-balance', component: GiBodyBalance, keywords: ['body', 'wellness', 'بدن', 'تندرستی'] },

  // --- Oral & men's grooming ---
  { name: 'gi-toothbrush', component: GiToothbrush, keywords: ['toothbrush', 'oral', 'مسواک', 'دهان و دندان'] },
  { name: 'gi-razor', component: GiRazor, keywords: ['razor', 'shave', 'تیغ', 'اصلاح'] },
  { name: 'gi-beard', component: GiBeard, keywords: ['beard', 'men', 'ریش', 'آقایان'] },
  { name: 'gi-male', component: GiMale, keywords: ['men', 'male', 'آقایان', 'مردانه'] },
  { name: 'gi-female', component: GiFemale, keywords: ['women', 'female', 'بانوان', 'زنانه'] },

  // --- Natural / organic ---
  { name: 'lu-leaf', component: LuLeaf, keywords: ['natural', 'organic', 'گیاهی', 'ارگانیک'] },
  { name: 'lu-sprout', component: LuSprout, keywords: ['vegan', 'natural', 'وگان', 'طبیعی'] },
  { name: 'gi-herbs', component: GiHerbsBundle, keywords: ['herbal', 'گیاهی', 'عصاره'] },
  { name: 'gi-leaf-swirl', component: GiLeafSwirl, keywords: ['natural', 'طبیعی'] },
  { name: 'lu-flower', component: LuFlower, keywords: ['flower', 'floral', 'گل', 'گلی'] },
  { name: 'lu-flower-2', component: LuFlower2, keywords: ['flower', 'گل'] },
  { name: 'gi-rose', component: GiRose, keywords: ['rose', 'گل رز', 'رز'] },
  { name: 'gi-lotus', component: GiLotus, keywords: ['lotus', 'spa', 'نیلوفر', 'اسپا'] },
  { name: 'gi-sunflower', component: GiSunflower, keywords: ['sunflower', 'آفتابگردان'] },
  { name: 'gi-flower-pot', component: GiFlowerPot, keywords: ['plant', 'گلدان'] },
  { name: 'gi-flower-emblem', component: GiFlowerEmblem, keywords: ['flower', 'emblem', 'گل'] },
  { name: 'lu-cherry', component: LuCherry, keywords: ['fruit', 'scent', 'میوه‌ای', 'رایحه'] },
  { name: 'lu-feather', component: LuFeather, keywords: ['light', 'soft', 'سبک', 'لطیف'] },

  // --- Health / baby ---
  { name: 'lu-heart-pulse', component: LuHeartPulse, keywords: ['health', 'سلامت', 'بهداشتی'] },
  { name: 'gi-health', component: GiHealthNormal, keywords: ['health', 'hygiene', 'بهداشت'] },
  { name: 'gi-medicines', component: GiMedicines, keywords: ['supplement', 'pharmacy', 'مکمل', 'داروخانه'] },
  { name: 'lu-pill', component: LuPill, keywords: ['supplement', 'vitamin', 'مکمل', 'ویتامین'] },
  { name: 'lu-baby', component: LuBaby, keywords: ['baby', 'kids', 'کودک', 'نوزاد'] },
  { name: 'gi-baby-bottle', component: GiBabyBottle, keywords: ['baby', 'مادر و کودک'] },

  // --- Accessories / premium ---
  { name: 'gi-pearl-necklace', component: GiPearlNecklace, keywords: ['accessory', 'jewelry', 'اکسسوری', 'زیورآلات'] },
  { name: 'gi-sunglasses', component: GiSunglasses, keywords: ['sunglasses', 'عینک آفتابی'] },
  { name: 'gi-hand-bag', component: GiHandBag, keywords: ['bag', 'cosmetic bag', 'کیف لوازم آرایش', 'کیف'] },
  { name: 'gi-clothes', component: GiClothes, keywords: ['clothes', 'fashion', 'پوشاک', 'مد'] },
  { name: 'gi-cut-diamond', component: GiCutDiamond, keywords: ['luxury', 'premium', 'لوکس', 'الماس'] },
  { name: 'gi-diamonds', component: GiDiamonds, keywords: ['premium', 'الماس'] },
  { name: 'lu-gem', component: LuGem, keywords: ['luxury', 'premium', 'لوکس', 'جواهر'] },
  { name: 'gi-crown', component: GiCrown, keywords: ['luxury', 'royal', 'تاج', 'ویژه'] },
  { name: 'lu-crown', component: LuCrown, keywords: ['vip', 'premium', 'ویژه'] },
  { name: 'gi-sparkles', component: GiSparkles, keywords: ['sparkle', 'glow', 'shine', 'درخشش'] },
  { name: 'lu-sparkles', component: LuSparkles, keywords: ['new', 'glow', 'جدید', 'درخشان'] },
  { name: 'lu-badge-check', component: LuBadgeCheck, keywords: ['original', 'authentic', 'اصل', 'اصالت'] },

  // --- Commerce / shopping ---
  { name: 'lu-shopping-cart', component: LuShoppingCart, keywords: ['cart', 'shopping', 'سبدخرید'] },
  { name: 'lu-shopping-bag', component: LuShoppingBag, keywords: ['bag', 'کیف‌خرید'] },
  { name: 'lu-store', component: LuStore, keywords: ['store', 'shop', 'فروشگاه'] },
  { name: 'lu-truck', component: LuTruck, keywords: ['shipping', 'delivery', 'ارسال', 'تحویل'] },
  { name: 'lu-tag', component: LuTag, keywords: ['tag', 'label', 'برچسب'] },
  { name: 'lu-percent', component: LuPercent, keywords: ['discount', 'percent', 'تخفیف', 'درصد'] },
  { name: 'lu-gift', component: LuGift, keywords: ['gift', 'gift set', 'هدیه', 'ست هدیه'] },
  { name: 'gi-party-popper', component: GiPartyPopper, keywords: ['party', 'celebration', 'جشن'] },
  { name: 'lu-ticket', component: LuTicket, keywords: ['ticket', 'voucher', 'بلیط', 'کوپن'] },
  { name: 'lu-wallet', component: LuWallet, keywords: ['wallet', 'کیف‌پول'] },
  { name: 'lu-credit-card', component: LuCreditCard, keywords: ['credit', 'card', 'کارت‌اعتباری'] },
  { name: 'lu-banknote', component: LuBanknote, keywords: ['money', 'cash', 'پول'] },
  { name: 'lu-trending-up', component: LuTrendingUp, keywords: ['trending', 'popular', 'پرفروش', 'رو به رشد'] },
  { name: 'lu-flame', component: LuFlame, keywords: ['hot', 'fire', 'trending', 'داغ', 'آتش'] },
  { name: 'gi-flame', component: GiFlame, keywords: ['fire', 'hot', 'آتش'] },
  { name: 'lu-zap', component: LuZap, keywords: ['fast', 'instant', 'flash', 'سریع', 'برق'] },
  { name: 'lu-star', component: LuStar, keywords: ['star', 'favorite', 'ستاره'] },
  { name: 'lu-heart', component: LuHeart, keywords: ['heart', 'wishlist', 'علاقه‌مندی'] },
  { name: 'lu-bookmark', component: LuBookmark, keywords: ['bookmark', 'save', 'نشانه'] },

  // --- General / utility ---
  { name: 'lu-smartphone', component: LuSmartphone, keywords: ['phone', 'mobile', 'app', 'موبایل', 'اپ'] },
  { name: 'fa-googleplay', component: FaGooglePlay, keywords: ['google play', 'android', 'گوگل‌پلی'] },
  { name: 'fa-appstore', component: FaAppStoreIos, keywords: ['app store', 'ios', 'apple', 'اپ‌استور'] },
  { name: 'fa-apple', component: FaApple, keywords: ['apple', 'ios', 'mac', 'اپل'] },
  { name: 'fa-android', component: FaAndroid, keywords: ['android', 'اندروید'] },
  { name: 'lu-bell', component: LuBell, keywords: ['notifications', 'اعلان'] },
  { name: 'lu-message', component: LuMessageCircle, keywords: ['chat', 'message', 'چت', 'پیام'] },
  { name: 'lu-mail', component: LuMail, keywords: ['email', 'mail', 'ایمیل'] },
  { name: 'lu-phone', component: LuPhone, keywords: ['call', 'phone', 'تماس'] },
  { name: 'lu-user', component: LuUser, keywords: ['user', 'account', 'کاربر'] },
  { name: 'lu-users', component: LuUsers, keywords: ['team', 'family', 'تیم', 'خانواده'] },
  { name: 'lu-shield', component: LuShield, keywords: ['shield', 'security', 'امنیت'] },
  { name: 'lu-lock', component: LuLock, keywords: ['lock', 'secure', 'قفل'] },
  { name: 'lu-settings', component: LuSettings, keywords: ['settings', 'تنظیمات'] },
  { name: 'lu-home', component: LuHouse, keywords: ['home', 'خانه'] },
  { name: 'lu-book', component: LuBookOpen, keywords: ['book', 'guide', 'کتاب', 'راهنما'] },
  { name: 'lu-file', component: LuFileText, keywords: ['file', 'document', 'فایل'] },
  { name: 'lu-image', component: LuImage, keywords: ['image', 'photo', 'تصویر'] },
  { name: 'lu-globe', component: LuGlobe, keywords: ['global', 'imported', 'خارجی', 'جهانی'] },
  { name: 'lu-coffee', component: LuCoffee, keywords: ['coffee', 'caffeine', 'قهوه', 'کافئین'] },
  { name: 'lu-fitness', component: LuDumbbell, keywords: ['fitness', 'sport', 'ورزشی', 'باشگاه'] },
  { name: 'lu-cloud-download', component: LuCloudDownload, keywords: ['download', 'cloud', 'دانلود'] },
  { name: 'gi-magnifying-glass', component: GiMagnifyingGlass, keywords: ['search', 'جستجو'] },
  { name: 'gi-key', component: GiKey, keywords: ['key', 'کلید'] },
  { name: 'gi-padlock', component: GiPadlock, keywords: ['lock', 'private', 'قفل'] },
  { name: 'gi-bookmark', component: GiBookmark, keywords: ['bookmark', 'نشانه'] },
  { name: 'gi-info', component: GiInfo, keywords: ['info', 'help', 'اطلاعات'] },
  { name: 'gi-help', component: GiHelp, keywords: ['help', 'support', 'کمک'] },
  { name: 'gi-check', component: GiCheckMark, keywords: ['check', 'done', 'تیک', 'تمام'] },
];

export const ICON_MAP: Record<string, IconType> = ICON_REGISTRY.reduce(
  (acc, entry) => {
    acc[entry.name] = entry.component;
    return acc;
  },
  {} as Record<string, IconType>
);

export const findIconByName = (name?: string | null): CategoryIconComponent | null => {
  if (!name) return null;
  const Icon = ICON_MAP[name];
  return (Icon as unknown as CategoryIconComponent) || null;
};
